import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { Container, Section, Eyebrow, DisplayHeading, BodyCopy, Button, Reveal } from "@/components/kit";
import { MaskedLinesInView } from "@/components/MaskedLines";
import Certificate from "@/components/Certificate";
import { scaleIn, viewport } from "@/lib/motion";

const DEMO_CERT = {
  certificate_id: "CERT-DEMO-000148",
  organization: "Demo Organization",
  ai_system: "Customer Support Agent",
  assurance_level: "Governed",
  status: "valid",
  issued_date: "2026-08-14",
  valid_until: "2027-08-14",
  assessment_type: "Security + Governance Review",
  frameworks: ["ISO/IEC 42001", "NIST AI RMF", "OWASP LLM", "EU AI Act"],
  trust_score: 87,
  verification_hash: "a7f3c9e21b4d88f6e0c5a91d7b2e4f80c6a1d93e",
  sample: true,
};

const AUDIENCE = ["Customer", "Partner", "Procurement", "Auditor", "Board", "Regulator"];
const STEPS = ["Checking record", "Scope confirmed", "Status current", "Verified"];

function VerifyFlow() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const [step, setStep] = useState(-1);
  useEffect(() => {
    if (!inView) return;
    let i = -1;
    const t = setInterval(() => {
      i += 1;
      setStep(i);
      if (i >= STEPS.length - 1) clearInterval(t);
    }, 650);
    return () => clearInterval(t);
  }, [inView]);
  return (
    <div ref={ref} className="mt-5 flex flex-col gap-2.5">
      <div className="font-mono text-[10px] tracking-wide text-brand-soft">CERT-DEMO-000148</div>
      {STEPS.map((s, i) => {
        const done = i <= step;
        const last = i === STEPS.length - 1;
        return (
          <div key={s} className="flex items-center gap-3">
            <span className={`flex h-4 w-4 items-center justify-center rounded-full border transition-colors duration-300 ${done ? (last ? "border-valid bg-valid/20" : "border-brand-soft bg-brand/20") : "border-line-bright"}`}>
              {done && <Check size={10} className={last ? "text-valid" : "text-brand-soft"} strokeWidth={3} />}
            </span>
            <span className={`font-mono text-[11px] uppercase tracking-[0.14em] transition-colors duration-300 ${done ? (last ? "text-valid" : "text-content") : "text-content-faint"}`}>{s}</span>
          </div>
        );
      })}
    </div>
  );
}

function TiltCertificate() {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rx = useSpring(useTransform(y, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 15 });
  const ry = useSpring(useTransform(x, [-0.5, 0.5], [-6, 6]), { stiffness: 120, damping: 15 });
  const reduce = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const onMove = (e) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - r.left) / r.width - 0.5);
    y.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { x.set(0); y.set(0); };
  return (
    <motion.div variants={scaleIn} initial="hidden" whileInView="show" viewport={viewport} onMouseMove={onMove} onMouseLeave={onLeave} style={{ perspective: 1200 }}>
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}>
        <Certificate cert={DEMO_CERT} />
      </motion.div>
    </motion.div>
  );
}

export default function Proof() {
  return (
    <Section id="proof" className="relative overflow-hidden border-t border-line bg-panel-raised/50">
      <div className="pointer-events-none absolute -right-40 top-0 h-[520px] w-[520px] rounded-full bg-brand/[0.04] blur-[130px]" />
      <div className="pointer-events-none absolute inset-0 tech-grid opacity-30" />
      <Container className="relative">
        <div className="grid gap-10 sm:gap-14 lg:grid-cols-[1fr_1fr] lg:items-center">
          <div>
            <Reveal><Eyebrow tone="brand">Verifiable proof</Eyebrow></Reveal>
            <DisplayHeading className="mt-6 text-3xl leading-[1.08] sm:text-4xl md:text-5xl">
              <MaskedLinesInView lines={["Don’t ask people", "to trust a dashboard."]} />
              <span className="mt-1 block text-gradient"><MaskedLinesInView lines={["Give them proof."]} /></span>
            </DisplayHeading>
            <Reveal i={1}>
              <BodyCopy className="mt-6">
                Every Meira assessment produces a certificate anyone can check — no login, no account.
                Enter an ID and confirm its scope and current status in seconds.
              </BodyCopy>
            </Reveal>

            <Reveal i={2}>
              <div className="mt-7 rounded-lg border border-line bg-panel-raised/60 p-5 sm:mt-8 sm:p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-content-faint">Anyone can verify</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {AUDIENCE.map((a) => (
                    <span key={a} className="rounded-full border border-line bg-panel-raised px-3 py-1 text-sm text-content-muted">{a}</span>
                  ))}
                </div>
                <VerifyFlow />
              </div>
            </Reveal>

            <Reveal i={3}>
              <div className="mt-8 flex max-w-xs flex-col items-stretch gap-3 sm:max-w-none sm:flex-row sm:flex-wrap sm:gap-4">
                <Button to="/verify" variant="primary" data-testid="proof-verify" className="uppercase tracking-[0.12em]">See How Verification Works</Button>
                <Button to="/assurance" variant="outline">Assurance &amp; Proof</Button>
              </div>
            </Reveal>
          </div>

          <TiltCertificate />
        </div>
      </Container>
    </Section>
  );
}
